import { useRef, useState } from "react";
import { Check, ChevronRight, CircleAlert, CircleDashed, FileUp, MessageSquare, Send, ShieldAlert, UserRound } from "lucide-react";

const STAGE_LABEL = { review: "In credit review", query: "Query raised", declined: "Declined", appeal: "Under appeal", sanctioned: "Sanctioned" };

function QueryCard({ q, onRespond }) {
  const [reply, setReply] = useState("");
  const fileRef = useRef(null);
  return <div className={`query-card ${q.status}`} data-testid={`query-${q.id}`}>
    <div className="query-head">
      <span className="query-type">{q.type === "document" ? <FileUp size={12} /> : <MessageSquare size={12} />} {q.type === "document" ? "Document request" : "Clarification"}</span>
      <span className={`status-pill ${q.status === "open" ? "wip" : "ready"}`}>{q.status === "open" ? "Open" : q.status === "responded" ? "Responded" : "Resolved"}</span>
    </div>
    <strong>{q.title}</strong>
    <p>{q.msg}</p>
    <small>Raised by {q.by} · {q.date}</small>
    {q.status === "open" ? (q.type === "document" ? <>
      <button className="ghost-btn" onClick={() => fileRef.current.click()} data-testid={`upload-response-${q.id}`}><FileUp size={13} /> Upload document</button>
      <input ref={fileRef} type="file" hidden onChange={(e) => e.target.files[0] && onRespond(q.id, `Uploaded “${e.target.files[0].name}”`)} data-testid={`upload-input-${q.id}`} />
    </> : <div className="reply-row"><input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your clarification…" data-testid={`reply-input-${q.id}`} /><button className="mini-btn solid" disabled={!reply.trim()} onClick={() => onRespond(q.id, reply.trim())} data-testid={`send-reply-${q.id}`}><Send size={12} /> Send</button></div>)
      : <div className="query-response" data-testid={`query-response-${q.id}`}><Check size={12} /> {q.response}</div>}
  </div>;
}

export default function StatusView({ app, data, onRespond, onAppeal }) {
  const openQ = data.queries.filter((q) => q.status === "open").length;
  return <div className="content-wrap">
    <div className="section-heading">
      <div><span className="eyebrow">POST-SUBMISSION / {app.id}</span><h2>{app.name}'s application</h2><p>{app.programme} · {app.university} · Submitted with {app.ftr}% FTR</p></div>
      <span className={`status-pill big hero ${data.stage}`} data-testid="application-stage-pill">{STAGE_LABEL[data.stage]}</span>
    </div>
    {data.stage === "declined" && <div className="decline-banner" data-testid="decline-banner">
      <ShieldAlert size={18} />
      <div><strong>Application declined by credit</strong><span>{data.decision.reason}</span></div>
      <button className="primary-btn" onClick={onAppeal} data-testid="appeal-button">Appeal & revisit <ChevronRight size={15} /></button>
    </div>}
    {data.stage === "appeal" && <div className="appeal-banner" data-testid="appeal-banner"><Check size={15} /> Appeal submitted — the credit committee will revisit this application. Grounds shared: “{data.decision.appeal}”</div>}
    <div className="status-grid">
      <div className="review-block" data-testid="status-timeline">
        <div className="block-heading"><div><span className="eyebrow">STATUS</span><h3>Where it stands</h3></div></div>
        <div className="tl">
          {data.timeline.map((t) => <div key={t.label} className={`tl-node ${t.done ? "done" : ""} ${t.current ? "current" : ""} ${t.bad ? "bad" : ""}`}>
            <span className="tl-dot">{t.done ? (t.bad ? <CircleAlert size={11} /> : <Check size={11} />) : <CircleDashed size={11} />}</span>
            <div><strong>{t.label}</strong><small>{t.date}</small></div>
          </div>)}
        </div>
      </div>
      <div className="review-block" data-testid="queries-block">
        <div className="block-heading"><div><span className="eyebrow">CREDIT QUERIES</span><h3>{openQ > 0 ? `${openQ} need${openQ === 1 ? "s" : ""} your response` : "All queries answered"}</h3></div>{openQ > 0 && <span className="review-count">{openQ} open</span>}</div>
        {data.queries.map((q) => <QueryCard key={q.id} q={q} onRespond={onRespond} />)}
        {data.queries.length === 0 && <div className="empty-docs" style={{ marginTop: 14 }}>No queries raised yet.</div>}
      </div>
      <div className="ftr-panel" data-testid="assignment-panel">
        <div className="ftr-top"><span className="ftr-icon"><UserRound size={18} /></span><span><span className="eyebrow">ASSIGNED TO</span><h3>{data.assignee.name}</h3></span></div>
        <p>{data.assignee.role} · picked this file up {data.assignee.since}.</p>
        <div style={{ marginTop: 6 }}>
          <div className="nav-stat"><span>SLA</span><b>{data.sla}</b></div>
          <div className="nav-stat"><span>FTR at submission</span><b>{app.ftr}%</b></div>
          <div className="nav-stat"><span>Participants</span><b>{app.parties}</b></div>
          <div className="nav-stat"><span>Documents</span><b>{app.docs}</b></div>
          <div className="nav-stat"><span>Open queries</span><b>{openQ}</b></div>
        </div>
        <div className="ftr-footer" style={{ marginTop: "auto" }}><span>Need help? Credit desk · credit.ops@credila.in</span></div>
      </div>
    </div>
    <div className="bottom-cta">
      <span>{openQ > 0 ? <><CircleAlert size={15} /> {openQ} open quer{openQ === 1 ? "y" : "ies"} — respond to keep the file moving</> : data.stage === "appeal" ? <><Check size={15} /> Appeal in review — we'll notify you on a decision</> : <><Check size={15} /> Nothing pending from your side</>}</span>
    </div>
  </div>;
}
